import { Inject, Injectable } from "@angular/core";
import { WindowToken } from "@app/util/window.token";

export const AuthStorageKeys = {
    ID_TOKEN: "hoapay_idToken",
    ACCESS_TOKEN: "hoapay_accessToken",
    EXPIRES_IN: "hoapay_expiresIn",
    HAS_RECEIVED_TOKEN: "hoapay_hasReceivedToken",
    SAVED_LOCATION: "hoapay_savedLocation",
    SSO_LOGIN_ATTEMPTS: "sso_login_attempts",
    LOGIN_REDIRECT_ATTEMPTS: "hoapass_login_redirects_attempts",
    INITIAL_LOGIN: "hoapay_initialLogin",
    SSO_IDENTITY: "sso_identity",
    SSO_SELECTED_EMAIL: "sso_selected_email",
    SSO_SELECTED_PROFILE_ID: "sso_selected_profile_id",
} as const;

export interface AuthTokenData {
    id_token: string;
    access_token: string;
    expires_in: string;
    account_username: string;
}

export interface SsoIdentity {
    userId: string; // the homeowner id (hoahomeowner)
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
}

@Injectable({
    providedIn: "root",
})
export class AuthTokenService {
    constructor(@Inject(WindowToken) private window: Window) {}

    /**
     * Stores authentication tokens received from the identity provider.
     * @param tokenData The token data parsed from the URL fragment.
     */
    storeTokens(tokenData: AuthTokenData): void {
        const timePeriod = 60000; // 1 minute in milliseconds
        const expiresIn = String(Number(tokenData.expires_in) - timePeriod);

        this.window.sessionStorage.setItem(
            AuthStorageKeys.EXPIRES_IN,
            expiresIn
        );
        this.window.sessionStorage.setItem(
            AuthStorageKeys.ID_TOKEN,
            tokenData.id_token
        );
        this.window.localStorage.setItem(
            AuthStorageKeys.ID_TOKEN,
            tokenData.id_token
        ); // Preserving original behavior of storing in both
        this.window.sessionStorage.setItem(
            AuthStorageKeys.ACCESS_TOKEN,
            tokenData.access_token
        );
        this.window.sessionStorage.setItem(
            AuthStorageKeys.HAS_RECEIVED_TOKEN,
            "true"
        );
    }

    /**
     * Retrieves the ID token from session storage.
     */
    getIdToken(): string | null {
        return this.window.sessionStorage.getItem(AuthStorageKeys.ID_TOKEN);
    }

    /**
     * Retrieves and immediately removes a saved URL for post-login redirection.
     */
    getAndClearSavedLocation(): string | null {
        const savedUrl = this.window.sessionStorage.getItem(
            AuthStorageKeys.SAVED_LOCATION
        );
        if (savedUrl) {
            this.window.sessionStorage.removeItem(
                AuthStorageKeys.SAVED_LOCATION
            );
        }
        return savedUrl;
    }

    /**
     * Checks if the current session was initiated from an SSO flow.
     */
    isSsoAttempt(): boolean {
        return !!this.window.sessionStorage.getItem(
            AuthStorageKeys.SSO_LOGIN_ATTEMPTS
        );
    }

    /**
     * Clears all session data. In the original code, this was `sessionStorage.clear()`.
     */
    clearAllSessionData(): void {
        this.window.sessionStorage.clear();
    }

    /**
     * Clears specific flags related to login attempts, as seen in the original "access denied" path.
     */
    clearLoginAttemptFlags(): void {
        this.window.sessionStorage.removeItem(
            AuthStorageKeys.LOGIN_REDIRECT_ATTEMPTS
        );
        this.window.sessionStorage.removeItem(AuthStorageKeys.INITIAL_LOGIN);
    }

    /**
     * Captured from the OAuth callback for a vendor-SSO arrival with no profile yet.
     */
    setSsoIdentity(identity: SsoIdentity): void {
        this.window.sessionStorage.setItem(
            AuthStorageKeys.SSO_IDENTITY,
            JSON.stringify(identity)
        );
    }

    /**
     * The SSO identity the entry and create-profile screens inherit. Falls back to a
     * demo value when navigating directly (demo only).
     */
    getSsoIdentity(): SsoIdentity {
        const raw = this.window.sessionStorage.getItem(
            AuthStorageKeys.SSO_IDENTITY
        );
        return raw
            ? (JSON.parse(raw) as SsoIdentity)
            : (null as unknown as SsoIdentity);
    }

    /**
     * Check if the SSO identity is available.
     */
    hasSsoIdentity(): boolean {
        return this.getSsoIdentity() !== null;
    }

    /**
    * The existing profile the user picked on the search screen: its `id` is the
    * OTP-validate target, and its `email` is what the verify screen displays.
    */
    setSsoSelectedProfile(id: number, email: string): void {
        this.window.sessionStorage.setItem(
            AuthStorageKeys.SSO_SELECTED_PROFILE_ID,
            String(id)
        );
        this.window.sessionStorage.setItem(
            AuthStorageKeys.SSO_SELECTED_EMAIL,
            email
        );
    }

    /**
     * Falls back to the SSO email when navigating directly (no profile selected).
     */
    getSsoSelectedEmail(): string {
        return (
            this.window.sessionStorage.getItem(
                AuthStorageKeys.SSO_SELECTED_EMAIL
            ) ?? this.getSsoIdentity().email
        );
    }

    /**
     * Falls back to the arriving homeowner id for direct navigation (sandbox only).
     */
    getSsoSelectedProfileId(): number {
        const raw = this.window.sessionStorage.getItem(
            AuthStorageKeys.SSO_SELECTED_PROFILE_ID
        );
        return raw !== null
            ? Number(raw)
            : Number(this.getSsoIdentity().userId);
    }
}
